// app/src/lib/procurement/contracts.js — VendorContract CRUD (US-2.8).

import { db } from '../db.js';
import { requireUserId, cents, addDays, isoToday } from './util.js';
import { mutate } from '../scale/instrument.js';

/** @typedef {{ contractId:string, userId:string, vendorId:string, itemId:string, agreedPriceCents:number, uom:string, effectiveFrom:string, effectiveThrough:string, notes?:string }} VendorContract */

export async function create(input) {
  const userId = await requireUserId();
  if (!input.vendorId || !input.itemId) throw new Error('contract.create: vendorId+itemId required');
  if (!Number.isFinite(input.agreedPrice) || input.agreedPrice <= 0) throw new Error('contract.create: invalid agreedPrice');
  /** @type {VendorContract} */
  const c = {
    contractId: input.contractId || crypto.randomUUID(),
    userId,
    vendorId: input.vendorId,
    itemId: input.itemId,
    agreedPriceCents: cents(input.agreedPrice),
    uom: input.uom || 'each',
    effectiveFrom: input.effectiveFrom || isoToday(),
    effectiveThrough: input.effectiveThrough || addDays(isoToday(), 365),
    notes: input.notes,
  };
  if (c.effectiveThrough < c.effectiveFrom) throw new Error('contract: effectiveThrough before effectiveFrom');
  return mutate(
    { entityType: 'vendorContract', entityId: c.contractId, action: 'create', after: c },
    async () => { await db.vendorContracts.put(c); return c; },
  );
}

export async function update(contractId, patch) {
  const userId = await requireUserId();
  const existing = await db.vendorContracts.get(contractId);
  if (!existing || existing.userId !== userId) throw new Error('contract.update: not found');
  const next = {
    ...existing,
    ...patch,
    agreedPriceCents: patch.agreedPrice != null ? cents(patch.agreedPrice) : existing.agreedPriceCents,
  };
  if (next.effectiveThrough < next.effectiveFrom) throw new Error('contract: effectiveThrough before effectiveFrom');
  return mutate(
    { entityType: 'vendorContract', entityId: contractId, action: 'update', before: existing, after: next },
    async () => { await db.vendorContracts.put(next); return next; },
  );
}

export async function remove(contractId) {
  const userId = await requireUserId();
  const existing = await db.vendorContracts.get(contractId);
  if (!existing || existing.userId !== userId) return;
  await mutate(
    { entityType: 'vendorContract', entityId: contractId, action: 'delete', before: existing },
    async () => { await db.vendorContracts.delete(contractId); },
  );
}

/** List a vendor's contracts: active and expired. */
export async function forVendor(vendorId) {
  const userId = await requireUserId();
  const today = isoToday();
  const rows = await db.vendorContracts.where('vendorId').equals(vendorId).filter((c) => c.userId === userId).toArray();
  return rows.map((c) => ({
    ...c,
    active: c.effectiveFrom <= today && today <= c.effectiveThrough,
  }));
}

/** Return the active contract for (vendor, item) at the given date, or null. */
export async function activeContract(vendorId, itemId, asOfIso = isoToday()) {
  const userId = await requireUserId();
  const rows = await db.vendorContracts
    .where('[vendorId+itemId]').equals([vendorId, itemId])
    .filter((c) => c.userId === userId && c.effectiveFrom <= asOfIso && c.effectiveThrough >= asOfIso)
    .toArray();
  if (!rows.length) return null;
  // If multiple, take the one with latest effectiveFrom.
  rows.sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
  return rows[0];
}
